import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { DiceBet } from '../types.js';
import type { SceneSelection } from '../scenes/types.js';
import { dicePayoutLabel } from './dicePayout.js';

interface Props {
  selection: SceneSelection | undefined;
  onChange: (s: SceneSelection) => void;
  disabled: boolean;
}

const TOTALS: DiceBet[] = Array.from({ length: 14 }, (_, i) => `total-${i + 4}` as DiceBet);
const FACES: number[] = [1, 2, 3, 4, 5, 6];

export function diceBetLabel(bet: DiceBet): string {
  if (bet === 'low') return 'LOW';
  if (bet === 'high') return 'HIGH';
  if (bet === 'triple-any') return 'ANY3';
  const m = /^(total|triple|pair|single)-(\d+)$/.exec(bet)!;
  const n = m[2];
  if (m[1] === 'total') return n;
  if (m[1] === 'triple') return `${n}·${n}·${n}`;
  if (m[1] === 'pair') return `${n}·${n}`;
  return n; // single
}

interface SpotProps {
  bet: DiceBet;
  active: boolean;
  disabled: boolean;
  onChange: (s: SceneSelection) => void;
  label?: string;
  /** Hide the payout multiplier line (compact totals row). */
  hidePayout?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

function Spot({ bet, active, disabled, onChange, label, hidePayout = false, size = 'md' }: SpotProps) {
  const minH = size === 'lg' ? 'min-h-[64px]' : size === 'sm' ? 'min-h-[36px]' : 'min-h-[52px]';
  const text = size === 'lg' ? 'text-[20px]' : size === 'sm' ? 'text-[13px]' : 'text-[15px]';
  return (
    <button
      type="button"
      data-bet={bet}
      data-active={active ? 'true' : 'false'}
      disabled={disabled}
      onClick={() => onChange({ kind: 'dice', bet })}
      className={[
        'group flex flex-col items-center justify-center gap-0.5 rounded-xl px-2 py-1.5 font-bold leading-none transition-all',
        minH,
        active
          ? 'bg-[var(--primary)] text-[var(--primary-on)] shadow-[0_0_18px_-2px_var(--primary)] ring-2 ring-[var(--primary)]'
          : 'bg-[var(--bg-elevated)] text-[var(--text-primary)] ring-1 ring-[var(--border)] hover:bg-[var(--bg-raised)] hover:ring-[var(--primary)]/50',
        disabled ? 'cursor-not-allowed opacity-40' : '',
      ].join(' ')}
    >
      <span className={text}>{label ?? diceBetLabel(bet)}</span>
      {!hidePayout && (
        <span
          className={[
            'text-[10px] font-semibold tabular-nums',
            active ? 'text-[var(--primary-on)]/85' : 'text-[var(--text-muted)]',
          ].join(' ')}
        >
          {dicePayoutLabel(bet)}
        </span>
      )}
    </button>
  );
}

function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
      {children}
    </span>
  );
}

export function DiceBoard({ selection, onChange, disabled }: Props) {
  const { t } = useTranslation('originals');
  const active = selection?.kind === 'dice' ? selection.bet : null;
  const spot = (bet: DiceBet, opts?: Omit<SpotProps, 'bet' | 'active' | 'disabled' | 'onChange'>) => (
    <Spot key={bet} bet={bet} active={active === bet} disabled={disabled} onChange={onChange} {...opts} />
  );

  return (
    <div className="flex flex-col gap-3 rounded-2xl bg-[var(--bg-sunken)]/60 p-3">
      {/* Primary bets — the headline LOW / HIGH row */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          data-bet="low"
          data-active={active === 'low' ? 'true' : 'false'}
          disabled={disabled}
          onClick={() => onChange({ kind: 'dice', bet: 'low' })}
          className={[
            'flex flex-col items-center justify-center gap-0.5 rounded-xl py-2.5 font-extrabold transition-all',
            active === 'low'
              ? 'bg-[var(--primary)] text-[var(--primary-on)] shadow-[0_0_22px_-2px_var(--primary)] ring-2 ring-[var(--primary)]'
              : 'bg-[var(--bg-elevated)] text-[var(--text-primary)] ring-1 ring-[var(--border)] hover:bg-[var(--bg-raised)] hover:ring-[var(--primary)]/60',
            disabled ? 'cursor-not-allowed opacity-40' : '',
          ].join(' ')}
        >
          <span className="text-[17px] tracking-wide">{t('dice.low')}</span>
          <span className="text-[11px] font-semibold tabular-nums opacity-80">
            {t('dice.lowHint')} · {dicePayoutLabel('low')}
          </span>
        </button>
        <button
          type="button"
          data-bet="high"
          data-active={active === 'high' ? 'true' : 'false'}
          disabled={disabled}
          onClick={() => onChange({ kind: 'dice', bet: 'high' })}
          className={[
            'flex flex-col items-center justify-center gap-0.5 rounded-xl py-2.5 font-extrabold transition-all',
            active === 'high'
              ? 'bg-[var(--primary)] text-[var(--primary-on)] shadow-[0_0_22px_-2px_var(--primary)] ring-2 ring-[var(--primary)]'
              : 'bg-[var(--bg-elevated)] text-[var(--text-primary)] ring-1 ring-[var(--border)] hover:bg-[var(--bg-raised)] hover:ring-[var(--primary)]/60',
            disabled ? 'cursor-not-allowed opacity-40' : '',
          ].join(' ')}
        >
          <span className="text-[17px] tracking-wide">{t('dice.high')}</span>
          <span className="text-[11px] font-semibold tabular-nums opacity-80">
            {t('dice.highHint')} · {dicePayoutLabel('high')}
          </span>
        </button>
      </div>

      {/* Total sum 4–17 — one row on wide screens, 7×2 on mobile */}
      <div className="flex flex-col gap-1.5">
        <GroupLabel>{t('dice.totals')}</GroupLabel>
        <div className="grid grid-cols-7 gap-1.5 md:grid-cols-[repeat(14,minmax(0,1fr))]">
          {TOTALS.map((bet) => spot(bet, { size: 'sm', hidePayout: true }))}
        </div>
      </div>

      {/* Triples (ตอง) — including ANY3 */}
      <div className="flex flex-col gap-1.5">
        <GroupLabel>{t('dice.bet.triple')}</GroupLabel>
        <div className="grid grid-cols-7 gap-1.5">
          {spot('triple-any', { label: t('dice.bet.any'), size: 'sm' })}
          {FACES.map((n) => spot(`triple-${n}` as DiceBet, { size: 'sm' }))}
        </div>
      </div>

      {/* Pairs (คู่) + singles (เดี่ยว) side by side on wide screens */}
      <div className="grid gap-3 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <GroupLabel>{t('dice.bet.pair')}</GroupLabel>
          <div className="grid grid-cols-6 gap-1.5">
            {FACES.map((n) => spot(`pair-${n}` as DiceBet, { size: 'sm' }))}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <GroupLabel>{t('dice.bet.single')}</GroupLabel>
          <div className="grid grid-cols-6 gap-1.5">
            {FACES.map((n) => spot(`single-${n}` as DiceBet, { size: 'sm' }))}
          </div>
        </div>
      </div>
    </div>
  );
}
